import React from 'react';
import '../../shared/ui/esqueleto.css';

export type EsqueletoVariante = 'texto' | 'titulo' | 'valor' | 'bloco';

interface EsqueletoProps {
  /** Altura nominal da peça, escolhida pelo conteúdo que ela substitui. */
  variante?: EsqueletoVariante;
  /** Largura em CSS (ex.: '70%', '120px'). Padrão: 100% do contêiner. */
  largura?: string;
  /** Altura em CSS, quando a variante não serve (ex.: um gráfico). */
  altura?: string;
  className?: string;
}

/**
 * Uma peça de esqueleto de carregamento (transversal, 16/09/2026).
 *
 * Substitui o texto estático ("Carregando regras...") por um placeholder com a forma do
 * conteúdo que vai chegar. `aria-hidden` de propósito: quem anuncia o carregamento é o
 * contêiner da seção, com `role="status"` — repetir o anúncio em cada peça faria o leitor
 * de tela ler dezenas de vezes "carregando".
 *
 * ⚠️ O placeholder deve ter a altura aproximada do conteúdo real. Menor que ele, a tela
 * pula quando o dado chega — que é o defeito que um esqueleto existe para evitar.
 */
export const Esqueleto: React.FC<EsqueletoProps> = ({
  variante = 'texto',
  largura,
  altura,
  className,
}) => (
  <span
    aria-hidden="true"
    className={`esqueleto esqueleto-${variante}${className ? ` ${className}` : ''}`}
    style={{ width: largura, height: altura }}
  />
);

interface EsqueletoLinhasProps {
  /** Quantas linhas desenhar. */
  linhas?: number;
  variante?: EsqueletoVariante;
  className?: string;
}

/** Bloco de N linhas — parágrafo, corpo de card, lista curta. */
export const EsqueletoLinhas: React.FC<EsqueletoLinhasProps> = ({
  linhas = 3,
  variante = 'texto',
  className,
}) => (
  <div className={`esqueleto-linhas${className ? ` ${className}` : ''}`} aria-hidden="true">
    {Array.from({ length: linhas }).map((_, i) => (
      // A última linha sai mais curta, como um parágrafo real termina.
      <Esqueleto key={i} variante={variante} largura={i === linhas - 1 ? '60%' : '100%'} />
    ))}
  </div>
);

interface EsqueletoTabelaProps {
  /** Quantas linhas de esqueleto desenhar (padrão: 5). */
  linhas?: number;
  /** Quantas colunas tem a tabela — precisa bater com o `<thead>`. */
  colunas: number;
}

/**
 * Corpo de tabela em carregamento: mantém o cabeçalho, as colunas e a altura da lista no
 * lugar, em vez de colapsar a tabela numa frase. Renderiza `<tr>`, então vai dentro de um
 * `<tbody>`.
 */
export const EsqueletoTabela: React.FC<EsqueletoTabelaProps> = ({ linhas = 5, colunas }) => (
  <>
    {Array.from({ length: linhas }).map((_, i) => (
      <tr key={i} aria-hidden="true">
        {Array.from({ length: colunas }).map((__, c) => (
          <td key={c}>
            {/* Larguras alternadas: coluna com todas as células do mesmo tamanho parece
                uma tabela de verdade mal formatada, não um carregamento. */}
            <Esqueleto
              variante="texto"
              className={`esqueleto-celula${c % 3 === 1 ? ' curta' : c % 3 === 2 ? ' longa' : ''}`}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

export default Esqueleto;
